<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, Navigation } from 'swiper/modules'

import WidgetLayout from '~/components/Widgets/WidgetLayout.vue'
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import getWidgetTranslatedMediaUrl from '~/utils/getWidgetTranslatedMediaUrl'

interface Props {
  brands?: {
    slug: string
    image: TranslatableString
  }[]
  backgroundColor?: string
}

const props = defineProps<Props>()
const { isPerkuJavTheme } = useTheme()

const swiperbreakpoints = {
  640: {
    slidesPerView: 3,
  },
  768: {
    slidesPerView: 4,
  },
  1024: {
    slidesPerView: 6,
  },
  1300: {
    slidesPerView: 8,
  },
}
</script>

<template>
  <WidgetLayout :background-color="props.backgroundColor" class-name="!pb-4">
    <div class="brands-slider m-auto max-w-2xlg">
      <ClientOnly>
        <Swiper
          :slides-per-view="2"
          :space-between="isPerkuJavTheme ? -1 : 30"
          :breakpoints="swiperbreakpoints"
          :modules="[Pagination, Navigation]"
          :navigation="true"
          :pagination="{ clickable: true, dynamicBullets: true }"
          :threshold="5">
          <SwiperSlide v-for="(brand, index) in props.brands" :key="index">
            <NuxtLink :to="brand.slug">
              <div class="brand">
                <img
                  v-if="getWidgetTranslatedMediaUrl(brand.image)"
                  :src="getWidgetTranslatedMediaUrl(brand.image)"
                  height="50px" />
              </div>
            </NuxtLink>
          </SwiperSlide>
        </Swiper>
      </ClientOnly>
    </div>
  </WidgetLayout>
</template>

<style lang="postcss">
.alt {
  .brands-slider {
    .brand {
      background-color: theme('colors.white') !important;
      border-radius: 0 !important;
      border: 1px solid theme('colors.neutral5');
    }
  }
}

.wl {
  .brands-slider {
    .brand {
      @apply bg-white dark:bg-dmGray1 border border-neutral5 shadow-md !rounded-md;
    }
  }
}
</style>

<style lang="postcss" scoped>
:deep(.brands-slider) {
  .swiper {
    padding-bottom: 3rem;
    user-select: none;
  }

  .brand {
    @apply bg-gray1 dark:bg-dmGray2;

    padding: 1rem;
    border-radius: theme('vars.borderRadiusLight');
    height: 5.125rem;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      @apply dark:invert;

      margin: auto;
      max-width: 100%;
      height: auto;
      max-height: 100%;
    }

    &:hover {
      @apply dark:bg-dmGray1 dark:shadow-md dark:shadow-gray-50;

      transition: all 0.1s linear;
      cursor: pointer;
      background-color: theme('colors.gray2');
    }
  }

  .swiper-button-next,
  .swiper-button-prev {
    bottom: 50%;
    margin: 0 1rem;
    display: none;
  }

  .swiper-pagination {
    bottom: 0px;
  }

  .swiper-pagination-bullet {
    border: theme('vars.borderMedium');

    &-active {
      opacity: 1;
      background: theme('colors.brand.primary');
      border-color: theme('colors.brand.primary');
    }
  }

  @screen lg {
    .swiper-button-next,
    .swiper-button-prev {
      display: flex;
    }
  }

  .swiper-button-disabled {
    display: none;
  }
}
</style>
